import {
	ArrowDown,
	ChevronSelectorVertical,
	Copy01,
	Edit01,
	HelpCircle,
	Trash01,
} from "@untitledui/icons";
import type {
	ComponentPropsWithRef,
	HTMLAttributes,
	ReactNode,
	Ref,
	TdHTMLAttributes,
	ThHTMLAttributes,
} from "react";
import { createContext, isValidElement, useContext } from "react";
import type {
	CellProps as AriaCellProps,
	ColumnProps as AriaColumnProps,
	RowProps as AriaRowProps,
	TableHeaderProps as AriaTableHeaderProps,
	TableProps as AriaTableProps,
} from "react-aria-components";
import {
	Cell as AriaCell,
	Collection as AriaCollection,
	Column as AriaColumn,
	Group as AriaGroup,
	Row as AriaRow,
	Table as AriaTable,
	TableBody as AriaTableBody,
	TableHeader as AriaTableHeader,
	useTableOptions,
} from "react-aria-components";
import { Badge } from "@/components/base/badges/badges";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import { Dropdown } from "@/components/base/dropdown/dropdown";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { cx } from "@/utils/cx";

export type TableColumn<T> = {
	id: string;
	label: string;
	minWidth?: number;
	render?: (item: T) => React.ReactNode;
};

export type TableAction<T> = {
	onClick: (item: T) => void;
	label: string | ((item: T) => string);
	icon?: React.ComponentType<{ className?: string }>;
	disabled?: boolean | ((item: T) => boolean);
};

export const TableRowActionsDropdown = () => (
	<Dropdown.Root>
		<Dropdown.DotsButton />

		<Dropdown.Popover className="w-min">
			<Dropdown.Menu>
				<Dropdown.Item icon={Edit01}>
					<span className="pr-4">Edit</span>
				</Dropdown.Item>
				<Dropdown.Item icon={Copy01}>
					<span className="pr-4">Copy link</span>
				</Dropdown.Item>
				<Dropdown.Item icon={Trash01}>
					<span className="pr-4">Delete</span>
				</Dropdown.Item>
			</Dropdown.Menu>
		</Dropdown.Popover>
	</Dropdown.Root>
);

export type TableSize = "sm" | "md";

/**
 * Apresentação da tabela.
 * - `default`: aparência original das primitivas, usada pelos consumidores que ainda não migraram.
 * - `listing`: padrão de listagem da aba Documentos de Veículos.
 */
export type TableVariant = "default" | "listing";

/**
 * Classes da referência de Documentos de Veículos, centralizadas para a variante `listing`.
 * `whitespace-nowrap` não faz parte da variante: cada consumidor decide se o conteúdo quebra linha.
 */
export const TABLE_LISTING_STYLES = {
	card: "rounded-lg bg-primary shadow-none",
	header: "bg-primary",
	head: "bg-primary px-2 pt-[8.75px] pb-[11.25px] first:pl-[35px] last:pr-[35px]",
	cell: "px-2 py-3.5 text-primary leading-5 first:pl-[35px] last:pr-[35px]",
	stateCell: "px-5 py-[22px] text-tertiary",
} as const;

type TableContextValue = {
	size?: TableSize;
	variant?: TableVariant;
};

const TableContext = createContext<TableContextValue | null>(null);

/** Resolve tamanho e variante efetivos, com os padrões das primitivas. */
const useTableContext = (): Required<TableContextValue> => {
	const context = useContext(TableContext);
	return {
		size: context?.size ?? "md",
		variant: context?.variant ?? "default",
	};
};

type TableCardRootProps = HTMLAttributes<HTMLDivElement> & {
	size?: TableSize;
	variant?: TableVariant;
};

const TableCardRoot = ({
	children,
	className,
	size = "md",
	variant,
	...props
}: TableCardRootProps) => (
	<TableContext.Provider value={{ size, variant }}>
		<div
			{...props}
			className={cx(
				"overflow-hidden rounded-xl bg-primary shadow-xs ring-1 ring-secondary",
				variant === "listing" && TABLE_LISTING_STYLES.card,
				className
			)}
			data-variant={variant}
		>
			{children}
		</div>
	</TableContext.Provider>
);

type TableCardHeaderProps = {
	/** The title of the table card header. */
	title: string;
	/** The badge displayed next to the title. */
	badge?: ReactNode;
	/** The description of the table card header. */
	description?: string;
	/** The content displayed after the title and badge. */
	contentTrailing?: ReactNode;
	/** The class name of the table card header. */
	className?: string;
};

const TableCardHeader = ({
	title,
	badge,
	description,
	contentTrailing,
	className,
}: TableCardHeaderProps) => {
	const { size } = useTableContext();

	return (
		<div
			className={cx(
				"relative flex flex-col items-start gap-4 border-secondary border-b bg-primary px-4 md:flex-row",
				size === "sm" ? "py-4 md:px-5" : "py-5 md:px-6",
				className
			)}
		>
			<div className="flex flex-1 flex-col gap-0.5">
				<div className="flex items-center gap-2">
					<h2
						className={cx(
							"font-semibold text-primary",
							size === "sm" ? "text-md" : "text-lg"
						)}
					>
						{title}
					</h2>
					{!!badge &&
						(isValidElement(badge) ? (
							badge
						) : (
							<Badge color="brand" size="sm">
								{badge}
							</Badge>
						))}
				</div>
				{!!description && (
					<p className="text-sm text-tertiary">{description}</p>
				)}
			</div>
			{contentTrailing}
		</div>
	);
};

interface TableRootProps
	extends AriaTableProps,
		Omit<ComponentPropsWithRef<"table">, "className" | "slot" | "style"> {
	/**
	 * Tamanho usado fora de `TableCard.Root`. Dentro de um card, o tamanho do card
	 * (padrão `md`) tem precedência, como antes.
	 */
	size?: TableSize;
	/** Variante da tabela. Quando informada, tem precedência sobre a variante do card. */
	variant?: TableVariant;
	/**
	 * Atualização em andamento com dados já exibidos. As linhas continuam visíveis,
	 * a região recebe `aria-busy` e um aviso é anunciado a leitores de tela.
	 */
	isRefreshing?: boolean;
	/** Texto do aviso de atualização. */
	refreshingLabel?: string;
}

const TableRoot = ({
	className,
	size = "md",
	variant,
	isRefreshing = false,
	refreshingLabel = "Atualizando dados",
	...props
}: TableRootProps) => {
	const cardContext = useContext(TableContext);
	const resolvedSize = cardContext?.size ?? size;
	const resolvedVariant = variant ?? cardContext?.variant ?? "default";
	const wrapperClassName = cx(
		"overflow-x-auto",
		isRefreshing ? "opacity-60 transition-opacity" : ""
	);

	return (
		<TableContext.Provider
			value={{ size: resolvedSize, variant: resolvedVariant }}
		>
			<div
				aria-busy={isRefreshing || undefined}
				className={wrapperClassName}
				data-refreshing={isRefreshing || undefined}
				data-size={resolvedSize}
				data-variant={resolvedVariant}
			>
				<AriaTable
					className={(state) =>
						cx(
							"w-full overflow-x-hidden",
							typeof className === "function" ? className(state) : className
						)
					}
					{...props}
				/>
			</div>
			<output aria-live="polite" className="sr-only">
				{isRefreshing ? refreshingLabel : ""}
			</output>
		</TableContext.Provider>
	);
};
TableRoot.displayName = "Table";

interface TableHeaderProps<T extends object>
	extends AriaTableHeaderProps<T>,
		Omit<
			ComponentPropsWithRef<"thead">,
			"children" | "className" | "slot" | "style"
		> {
	bordered?: boolean;
}

const TableHeader = <T extends object>({
	columns,
	children,
	bordered = true,
	className,
	...props
}: TableHeaderProps<T>) => {
	const { size, variant } = useTableContext();
	const { selectionBehavior, selectionMode } = useTableOptions();

	return (
		<AriaTableHeader
			{...props}
			className={(state) =>
				cx(
					"relative bg-secondary",
					size === "sm" ? "h-9" : "h-11",
					variant === "listing" && TABLE_LISTING_STYLES.header,

					// Row border: an "after" pseudo-element keeps the border from taking up space.
					!!bordered &&
						"[&>tr>th]:after:pointer-events-none [&>tr>th]:after:absolute [&>tr>th]:after:inset-x-0 [&>tr>th]:after:bottom-0 [&>tr>th]:after:h-px [&>tr>th]:after:bg-border-secondary [&>tr>th]:focus-visible:after:bg-transparent",

					typeof className === "function" ? className(state) : className
				)
			}
		>
			{selectionBehavior === "toggle" && (
				<AriaColumn
					className={cx(
						"relative py-2 pr-0 pl-4",
						size === "sm" ? "w-9 md:pl-5" : "w-11 md:pl-6"
					)}
				>
					{selectionMode === "multiple" && (
						<div className="flex items-start">
							<Checkbox size={size} slot="selection" />
						</div>
					)}
				</AriaColumn>
			)}
			<AriaCollection items={columns}>{children}</AriaCollection>
		</AriaTableHeader>
	);
};

TableHeader.displayName = "TableHeader";

interface TableHeadProps
	extends AriaColumnProps,
		Omit<
			ThHTMLAttributes<HTMLTableCellElement>,
			"children" | "className" | "style" | "id"
		> {
	label?: string;
	tooltip?: string;
}

const TableHead = ({
	className,
	tooltip,
	label,
	children,
	...props
}: TableHeadProps) => {
	const { variant } = useTableContext();
	const { selectionBehavior } = useTableOptions();

	return (
		<AriaColumn
			{...props}
			className={(state) =>
				cx(
					"relative p-0 px-6 py-2 outline-hidden focus-visible:z-1 focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-inset focus-visible:ring-offset-bg-primary",
					variant === "listing" && TABLE_LISTING_STYLES.head,
					selectionBehavior === "toggle" && "nth-2:pl-3",
					!!state.allowsSorting && "cursor-pointer",
					typeof className === "function" ? className(state) : className
				)
			}
		>
			{(state) => (
				<AriaGroup className="flex items-center gap-1">
					<div className="flex items-center gap-1">
						{!!label && (
							<span
								className="whitespace-nowrap font-semibold text-primary text-xs"
								data-label={label}
							>
								{label}
							</span>
						)}
						{typeof children === "function" ? children(state) : children}
					</div>

					{!!tooltip && (
						<Tooltip placement="top" title={tooltip}>
							<TooltipTrigger className="cursor-pointer text-fg-quaternary transition duration-100 ease-linear hover:text-fg-quaternary_hover focus:text-fg-quaternary_hover">
								<HelpCircle className="size-4" />
							</TooltipTrigger>
						</Tooltip>
					)}

					{!!state.allowsSorting &&
						(state.sortDirection ? (
							<ArrowDown
								className={cx(
									"size-3 stroke-[3px] text-fg-quaternary",
									state.sortDirection === "ascending" && "rotate-180"
								)}
							/>
						) : (
							<ChevronSelectorVertical
								className="text-fg-quaternary"
								size={12}
								strokeWidth={3}
							/>
						))}
				</AriaGroup>
			)}
		</AriaColumn>
	);
};
TableHead.displayName = "TableHead";

interface TableRowProps<T extends object>
	extends AriaRowProps<T>,
		Omit<
			ComponentPropsWithRef<"tr">,
			"children" | "className" | "onClick" | "slot" | "style" | "id"
		> {
	highlightSelectedRow?: boolean;
}

const TableRow = <T extends object>({
	columns,
	children,
	className,
	highlightSelectedRow = true,
	...props
}: TableRowProps<T>) => {
	const { size } = useTableContext();
	const { selectionBehavior } = useTableOptions();

	return (
		<AriaRow
			{...props}
			className={(state) =>
				cx(
					"focus-visible:-outline-offset-2 relative border-border-secondary border-b border-solid outline-focus-ring transition-colors after:pointer-events-none hover:bg-secondary focus-visible:outline-2",
					size === "sm" ? "h-14" : "h-18",
					!!highlightSelectedRow && "selected:bg-secondary",

					typeof className === "function" ? className(state) : className
				)
			}
		>
			{selectionBehavior === "toggle" && (
				<AriaCell
					className={cx(
						"relative px-5 py-[22px]",
						size === "sm" ? "md:pl-5" : "md:pl-6"
					)}
				>
					<div className="flex items-end">
						<Checkbox size={size} slot="selection" />
					</div>
				</AriaCell>
			)}
			<AriaCollection items={columns}>{children}</AriaCollection>
		</AriaRow>
	);
};

TableRow.displayName = "TableRow";

interface TableCellProps
	extends AriaCellProps,
		Omit<
			TdHTMLAttributes<HTMLTableCellElement>,
			"children" | "className" | "style" | "id"
		> {
	ref?: Ref<HTMLTableCellElement>;
}

const TableCell = ({ className, children, ...props }: TableCellProps) => {
	const { size, variant } = useTableContext();
	const { selectionBehavior } = useTableOptions();

	return (
		<AriaCell
			{...props}
			className={(state) =>
				cx(
					"focus-visible:-outline-offset-2 relative text-sm text-tertiary outline-focus-ring focus-visible:z-1 focus-visible:outline-2",
					size === "sm" && "px-5 py-3",
					size === "md" && "px-5 py-[22px]",
					variant === "listing" && TABLE_LISTING_STYLES.cell,

					selectionBehavior === "toggle" && "nth-2:pl-3",

					typeof className === "function" ? className(state) : className
				)
			}
		>
			{children}
		</AriaCell>
	);
};
TableCell.displayName = "TableCell";

const TableCard = {
	Root: TableCardRoot,
	Header: TableCardHeader,
};

const Table = TableRoot as typeof TableRoot & {
	Body: typeof AriaTableBody;
	Cell: typeof TableCell;
	Head: typeof TableHead;
	Header: typeof TableHeader;
	Row: typeof TableRow;
};
Table.Body = AriaTableBody;
Table.Cell = TableCell;
Table.Head = TableHead;
Table.Header = TableHeader;
Table.Row = TableRow;

export { Table, TableCard };
